import { useMemo } from 'react'
import { useSelector } from 'react-redux'

import { selectUser } from 'core-components/sitebuilder/store/selectors/selectUser'
import { getAttentionStatusRole } from 'partner360/entities/attentionStatuses'

import { FINANCIAL_COCKPIT_TRANSLATION_KEYS } from '../../../../configs/translations'
import {
  buildAttentionGeoNodes,
  buildAttentionManagerTree,
  buildAttentionPartnerOverviews,
  buildAttentionPbmTree,
  buildAttentionPeopleTree,
  fromAttentionGeoNodes,
  getAttentionGeoStartDepth,
  getAttentionNavigatorModes,
  type IAttentionNavigatorNode,
  type TAttentionNavigatorMode,
} from '../../../../lib'
import {
  selectCockpitAccessLevel,
  selectCockpitAttentionManagerAssignments,
  selectCockpitAttentionOwnersByPartner,
  selectCockpitAttentionPartners,
  selectCockpitAttentionPbmAssignments,
  selectCockpitGeoMappingItems,
  selectCockpitMultiPartnerAttentionStatuses,
  selectCockpitUserAccess,
} from '../../../../model/selectors'

const KEYS = FINANCIAL_COCKPIT_TRANSLATION_KEYS.attentionStatuses.fullscreen

/**
 * Everything the full-screen view reads: every partner in view with the statuses it holds open,
 * who owns it, and the trees the navigator walks in each of the role's modes.
 */
export const useAttentionFullscreenData = () => {
  const statuses = useSelector(selectCockpitMultiPartnerAttentionStatuses)
  const partners = useSelector(selectCockpitAttentionPartners)
  const ownersByPartner = useSelector(selectCockpitAttentionOwnersByPartner)
  const pbmAssignments = useSelector(selectCockpitAttentionPbmAssignments)
  const managerAssignments = useSelector(selectCockpitAttentionManagerAssignments)
  const geoMappingItems = useSelector(selectCockpitGeoMappingItems)
  const accessLevel = useSelector(selectCockpitAccessLevel)
  const { role } = useSelector(selectCockpitUserAccess)
  const user = useSelector(selectUser)

  const modes = useMemo(() => getAttentionNavigatorModes(role), [role])

  const overviews = useMemo(
    () => buildAttentionPartnerOverviews(partners, statuses),
    [partners, statuses]
  )

  const trees = useMemo<Record<TAttentionNavigatorMode, IAttentionNavigatorNode[]>>(() => {
    const owners = { partners, pbmAssignments, managerAssignments }

    return {
      people: modes.includes('people')
        ? buildAttentionPeopleTree({
            ...owners,
            currentUserEmail: user?.email ?? '',
            labelKeys: { myPbms: KEYS.myPbms, otherManagers: KEYS.otherManagers },
          })
        : [],
      managers: modes.includes('managers') ? buildAttentionManagerTree(owners) : [],
      pbms: modes.includes('pbms') ? buildAttentionPbmTree(owners) : [],
      geo: modes.includes('geo')
        ? fromAttentionGeoNodes(
            buildAttentionGeoNodes({
              partners,
              geoMappingItems,
              startDepth: getAttentionGeoStartDepth(accessLevel),
            })
          )
        : [],
      partners: [],
    }
  }, [accessLevel, geoMappingItems, managerAssignments, modes, partners, pbmAssignments, user])

  /* The PBM works their own partners from this view; every other role reads it as an overview. */
  const isPbm = getAttentionStatusRole(role) === 'pbm'

  return { statuses, partners, overviews, ownersByPartner, modes, trees, isPbm }
}

export type TAttentionFullscreenData = ReturnType<typeof useAttentionFullscreenData>
